/**
 * DisputeWizardStepper - Shared 7-Step Progress Indicator
 * 
 * Shows the full dispute wizard flow:
 * 1. Select Items
 * 2. Add Context
 * 3. Review
 * 4. Generate Letters
 * 5. Add Supporting Documents
 * 6. Envelope Setup
 * 7. Send Mail
 */

import React from 'react';
import { Check, ChevronRight } from 'lucide-react';

const WIZARD_STEPS = [
  { num: 1, label: 'Select Items', shortLabel: 'Select' },
  { num: 2, label: 'Add Context', shortLabel: 'Context' },
  { num: 3, label: 'Review', shortLabel: 'Review' },
  { num: 4, label: 'Generate Letters', shortLabel: 'Generate' },
  { num: 5, label: 'Add Supporting Documents', shortLabel: 'Documents' },
  { num: 6, label: 'Envelope Setup', shortLabel: 'Envelope' },
  { num: 7, label: 'Send Mail', shortLabel: 'Send' },
];

// Full stepper with all 7 steps - Mobile responsive with horizontal scroll
export const DisputeWizardStepper = ({ 
  currentStep = 1, 
  compact = false,
  onStepClick = null,
  className = ''
}) => {
  return (
    <div className={`bg-stone-50 dark:bg-[rgba(8,32,24,0.65)] border-b border-stone-100 dark:border-[rgba(142,218,182,0.12)] py-2 sm:py-3 px-2 sm:px-4 ${className}`}>
      <div className="max-w-7xl mx-auto overflow-x-auto hide-scrollbar">
        <div className="flex items-center justify-start sm:justify-between min-w-max sm:min-w-0 gap-0.5 sm:gap-0">
          {WIZARD_STEPS.map((step, idx) => {
            const isActive = step.num === currentStep;
            const isDone = step.num < currentStep;
            const isClickable = onStepClick && isDone;
            
            return (
              <React.Fragment key={step.num}>
                <button
                  onClick={() => isClickable && onStepClick(step.num)}
                  disabled={!isClickable}
                  className={`flex items-center gap-1 sm:gap-2 whitespace-nowrap px-1 sm:px-2 py-1 rounded-lg transition-all ${
                    isClickable ? 'cursor-pointer hover:bg-stone-100 dark:hover:bg-[rgba(142,218,182,0.10)]' : ''
                  } ${!isClickable && !isActive && !isDone ? 'opacity-60' : ''}`}
                  data-testid={`wizard-step-${step.num}`}
                >
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold shrink-0 transition-colors ${
                    isDone 
                      ? 'bg-emerald-600 text-white' 
                      : isActive 
                        ? 'bg-emerald-600 text-white ring-2 ring-emerald-300 ring-offset-1 dark:ring-offset-[rgba(8,32,24,0.65)]' 
                        : 'bg-stone-200 dark:bg-[rgba(142,218,182,0.20)] text-stone-500 dark:text-[#93AE9F]'
                  }`}>
                    {isDone ? <Check size={10} className="sm:w-3 sm:h-3" /> : step.num}
                  </div>
                  <span className={`text-[11px] sm:text-sm ${
                    isActive 
                      ? 'font-semibold text-stone-900 dark:text-[#EAF7F0]' 
                      : isDone 
                        ? 'text-stone-700 dark:text-[#C7DDD0]' 
                        : 'text-stone-500 dark:text-[#93AE9F]'
                  }`}>
                    {step.shortLabel}
                  </span>
                </button>
                {idx < WIZARD_STEPS.length - 1 && (
                  <ChevronRight size={12} className="text-stone-300 dark:text-[rgba(142,218,182,0.30)] shrink-0 mx-0 sm:mx-1" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Compact stepper for Steps 5-7 (delivery flow) - Mobile responsive
export const DeliveryFlowStepper = ({ 
  currentStep = 5, 
  onStepClick = null,
  className = ''
}) => {
  const deliverySteps = WIZARD_STEPS.slice(4); // Steps 5-7
  
  return (
    <div className={`bg-stone-50 dark:bg-[rgba(8,32,24,0.65)] border-t border-stone-100 dark:border-[rgba(142,218,182,0.12)] py-2 px-2 sm:px-4 ${className}`}>
      <div className="max-w-7xl mx-auto overflow-x-auto hide-scrollbar">
        <div className="flex items-center gap-2 sm:gap-4 text-sm min-w-max sm:min-w-0">
          {deliverySteps.map((step, idx) => {
            const isActive = step.num === currentStep;
            const isDone = step.num < currentStep;
            const isClickable = onStepClick && isDone;
            
            return (
              <div key={step.num} className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => isClickable && onStepClick(step.num)}
                  disabled={!isClickable}
                  className={`flex items-center gap-1 sm:gap-2 ${isClickable ? 'cursor-pointer hover:opacity-80' : ''}`}
                  data-testid={`delivery-step-${step.num}`}
                >
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold ${
                    isDone || isActive 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-stone-200 dark:bg-[rgba(142,218,182,0.20)] text-stone-500 dark:text-[#93AE9F]'
                  }`}>
                    {isDone ? <Check size={10} /> : step.num}
                  </div>
                  <span className={`text-xs sm:text-sm whitespace-nowrap ${isActive 
                    ? 'font-medium text-stone-800 dark:text-[#EAF7F0]' 
                    : 'text-stone-500 dark:text-[#93AE9F]'
                  }`}>
                    {step.shortLabel}
                  </span>
                </button>
                {idx < deliverySteps.length - 1 && (
                  <ChevronRight size={12} className="text-stone-300 dark:text-[rgba(142,218,182,0.30)]" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Mini stepper for tight spaces
export const MiniStepper = ({ currentStep = 1, totalSteps = 7 }) => {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map(step => (
        <div
          key={step}
          className={`w-2 h-2 rounded-full transition-colors ${
            step <= currentStep 
              ? 'bg-emerald-500' 
              : 'bg-stone-300 dark:bg-[rgba(142,218,182,0.30)]'
          }`}
        />
      ))}
    </div>
  );
};

export default DisputeWizardStepper;
